import moment from 'moment';
import { type Dispatch, type SetStateAction, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type { DateRangeValue, FilterFieldConfig, FilterKind, MasterFilterValues } from './types';

/**
 * Reads a single value from URL params (or returns `undefined` if absent / malformed),
 * and writes a single value back to URL params (deleting the key when the value is empty).
 *
 * Each filter `FilterKind` declares its own codec so adding a new kind only requires adding
 * a new entry to `codecsByKind` below.
 */
interface UrlValueCodec {
  read: (params: URLSearchParams, paramKey: string) => unknown | undefined;
  write: (params: URLSearchParams, paramKey: string, value: unknown) => void;
}

const arrayCodec: UrlValueCodec = {
  read: (params, key) => {
    const raw = params.get(key);
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  },
  write: (params, key, value) => {
    params.delete(key);
    if (Array.isArray(value) && value.length > 0) params.set(key, JSON.stringify(value));
  },
};

const stringCodec: UrlValueCodec = {
  read: (params, key) => params.get(key) ?? undefined,
  write: (params, key, value) => {
    params.delete(key);
    if (typeof value === 'string' && value.length > 0) params.set(key, value);
  },
};

const dateRangeCodec: UrlValueCodec = {
  read: (params, key) => {
    const start = params.get(`${key}.start`);
    const end = params.get(`${key}.end`);
    if (!start && !end) return undefined;
    return {
      start: start ? moment(start) : null,
      end: end ? moment(end) : null,
    };
  },
  write: (params, key, value) => {
    params.delete(`${key}.start`);
    params.delete(`${key}.end`);
    const dr = value as DateRangeValue | undefined;
    if (dr?.start) params.set(`${key}.start`, dr.start.toISOString());
    if (dr?.end) params.set(`${key}.end`, dr.end.toISOString());
  },
};

const codecsByKind: Record<FilterKind, UrlValueCodec> = {
  multiSelect: arrayCodec,
  singleSelect: stringCodec,
  text: stringCodec,
  userSelect: stringCodec,
  dateRange: dateRangeCodec,
  metaDatePreset: dateRangeCodec,
};

const paramKeyOf = (namespace: string, fieldKey: string) => `${namespace}.${fieldKey}`;

const currentSearch = (): string => (typeof window !== 'undefined' ? window.location.search : '');

/** Read filter values from a URL search string for the given config + namespace. */
export const readValuesFromUrl = (
  config: FilterFieldConfig[],
  namespace: string,
  search: string = currentSearch()
): MasterFilterValues => {
  const params = new URLSearchParams(search);
  const values: MasterFilterValues = {};
  for (const field of config) {
    const value = codecsByKind[field.kind].read(params, paramKeyOf(namespace, field.key));
    if (value !== undefined) values[field.key] = value;
  }
  return values;
};

/** Mutate `params` in place so its namespaced keys reflect the given values. */
const writeValuesToParams = (
  config: FilterFieldConfig[],
  namespace: string,
  values: MasterFilterValues,
  params: URLSearchParams
): void => {
  for (const field of config) {
    codecsByKind[field.kind].write(params, paramKeyOf(namespace, field.key), values[field.key]);
  }
};

/**
 * Mounts URL persistence on top of an existing `[values, setValues]` pair.
 *
 * - Writes namespaced query params on every value change. Other (non-namespaced) params are
 *   left untouched, so callers can co-own the URL with us.
 * - When a new field shows up in `config` (e.g. a conditional field gated on async data),
 *   re-seeds its value from the URL — otherwise the write effect below would erase the
 *   not-yet-applied URL value before the user sees it.
 *
 * No-op when `namespace` is undefined.
 */
export const usePersistFilterValues = (
  config: FilterFieldConfig[],
  namespace: string | undefined,
  values: MasterFilterValues,
  setValues: Dispatch<SetStateAction<MasterFilterValues>>
): void => {
  const navigate = useNavigate();
  const seenKeysRef = useRef<Set<string>>(new Set(config.map((f) => f.key)));

  // Re-seed when new config fields appear.
  useEffect(() => {
    if (!namespace) return;
    const newFields = config.filter((f) => !seenKeysRef.current.has(f.key));
    if (newFields.length === 0) return;
    for (const f of newFields) seenKeysRef.current.add(f.key);
    const seeded = readValuesFromUrl(newFields, namespace);
    if (Object.keys(seeded).length === 0) return;
    setValues((prev) => ({ ...prev, ...seeded }));
  }, [config, namespace, setValues]);

  // Write values → URL on every change (preserving non-namespaced params).
  useEffect(() => {
    if (!namespace) return;
    const params = new URLSearchParams(window.location.search);
    const before = params.toString();
    writeValuesToParams(config, namespace, values, params);
    const after = params.toString();
    if (before === after) return;
    navigate({ pathname: window.location.pathname, search: after ? `?${after}` : '' }, { replace: true });
  }, [config, namespace, values, navigate]);
};
